/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output
} from '@angular/core';
import * as moment from 'moment/moment';

import {ACCRUAL_STATUS_CONFIG_NOT_FOUND, DATE_FORMAT} from '../../../../../app.constants';

export interface AbsenceEvent {
  type: string;
  id: number;
  attributes: {
    begin: string;
    end: string;
    type: string;
    beginDayfactor: number;
    endDayfactor: number;
    empContract: number;
    status?: string;
    name: string;
    transid: string;
    comment: string;
    pendingEntityId?: number;
    values: any;
    hoursPaid?: number;
    hoursWorked?: number;
    dirty?: boolean;
  };
  view: {
    color: string;
    borderColor?: string;
    transId: string;
    duration: number;
    readOnly: boolean;
    borderStyle: string;
  };
  duration?: any;
}

@Component({
  selector: 'gp-calendar-absence',
  templateUrl: './calendar-absence.component.html',
  styleUrls: ['./calendar-absence.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class CalendarAbsenceComponent implements OnChanges {

  public static RESIZE_ACTION_INCREASE_END: string = 'increaseEnd';
  public static RESIZE_ACTION_DECREASE_END: string = 'decreaseEnd';
  public static RESIZE_ACTION_INCREASE_START: string = 'increaseStart';
  public static RESIZE_ACTION_DECREASE_START: string = 'decreaseStart';

  @Input()
  public isReadOnly: boolean = true;

  public height: string;
  public marginTop: string;
  public isHover: boolean = false;
  public isBegining: boolean;
  public isEnding: boolean;
  public isSingleDay: boolean;
  public cellHeight: number = 15;

  @Input('event')
  public eventOpts: AbsenceEvent;

  @Input('showContent')
  public showContent: boolean = true;

  @Input('isTeamPlanning')
  public isTeamPlanning: boolean = false;

  @Input('weekview')
  public weekview: boolean = false;

  // it should be 8 for month view
  @Input('workedHours')
  public workedHours: number = 8;

  @Input('color')
  public color: string = '#fff';

  @Input('date')
  public date: string;

  @Output()
  public eventChanged: EventEmitter<AbsenceEvent> = new EventEmitter<AbsenceEvent>();

  @Output()
  public resizeEvent: EventEmitter<{ event: AbsenceEvent, action: string }>
    = new EventEmitter<{ event: AbsenceEvent, action: string }>();

  constructor(public element: ElementRef) {
  }

  public ngOnChanges() {
    if (this.eventOpts) {
      if (!this.isTeamPlanning) {
        this.cellHeight = this.weekview ? 43.3 : 15;
      } else {
        this.cellHeight = 12.5;
      }
      this.isBegining = moment(this.date).isSame(this.eventOpts.attributes.begin);
      this.isEnding = moment(this.date).isSame(this.eventOpts.attributes.end);
      this.isSingleDay = this.eventOpts.attributes.begin === this.eventOpts.attributes.end;
      if (this.isSingleDay) {
        if (this.eventOpts.attributes.beginDayfactor + this.eventOpts.attributes.endDayfactor > 1) {
          this.eventOpts.attributes.endDayfactor = 0;
        }
      }
      this.updateEventView();
    }
  }

  public getDuration(): string {
    return this.eventOpts.view.duration.toFixed(1);
  }

  public updateEventView() {
    this.marginTop = '0';
    let height: number = 100; // percents
    let marginTop = 0;

    if (this.isBegining) {
      height -= this.eventOpts.attributes.beginDayfactor * 100;
      marginTop = this.eventOpts.attributes.beginDayfactor * this.workedHours * this.cellHeight;
      if (!this.weekview && marginTop > 100) {
        marginTop = 100;
      }
      this.marginTop = `${marginTop}px`;
    }
    if (this.isEnding) {
      height -= this.eventOpts.attributes.endDayfactor * 100;
    }
    height = height * this.workedHours * this.cellHeight / 100;
    this.height = `${this.isTeamPlanning ? height - 1.5 : height - 2}px`;

    this.element.nativeElement.style.marginTop = this.marginTop;
    this.element.nativeElement.style.height = `${height}px`;
    if (this.isTeamPlanning) {
      this.element.nativeElement.style.marginLeft = '1%';
      this.element.nativeElement.style.width = '98%';

    }
  }

  public increaseEnd() {
    if (this.hasAccrualsConfiguration()) {
      this.resizeEvent.emit({event: this.eventOpts, action: CalendarAbsenceComponent.RESIZE_ACTION_INCREASE_END});
    } else {
      this.eventOpts.attributes.end = moment(this.eventOpts.attributes.end).add(1, 'days').format(DATE_FORMAT);
      this.eventOpts.attributes.endDayfactor = 0;
      this.eventChanged.emit(this.eventOpts);
    }
  }

  public decreaseEnd() {
    if (this.hasAccrualsConfiguration()) {
      this.resizeEvent.emit({event: this.eventOpts, action: CalendarAbsenceComponent.RESIZE_ACTION_DECREASE_END});
    } else {
      this.eventOpts.attributes.end = moment(this.eventOpts.attributes.end).add(-1, 'days').format(DATE_FORMAT);
      this.eventChanged.emit(this.eventOpts);
    }
  }

  public increaseStart() {
    if (this.hasAccrualsConfiguration()) {
      this.resizeEvent.emit({event: this.eventOpts, action: CalendarAbsenceComponent.RESIZE_ACTION_INCREASE_START});
    } else {
      this.eventOpts.attributes.begin = moment(this.eventOpts.attributes.begin).add(1, 'days').format(DATE_FORMAT);
      this.eventChanged.emit(this.eventOpts);
    }
  }

  public decreaseStart() {
    if (this.hasAccrualsConfiguration()) {
      this.resizeEvent.emit({event: this.eventOpts, action: CalendarAbsenceComponent.RESIZE_ACTION_DECREASE_START});
    } else {
      this.eventOpts.attributes.begin = moment(this.eventOpts.attributes.begin).add(-1, 'days').format(DATE_FORMAT);
      this.eventOpts.attributes.beginDayfactor = 0;
      this.eventChanged.emit(this.eventOpts);
    }
  }

  private hasAccrualsConfiguration() {
    return this.eventOpts.duration && this.eventOpts.duration.status !== ACCRUAL_STATUS_CONFIG_NOT_FOUND;
  }

  @HostListener('mouseenter')
  public mouseenter() {
    this.isHover = true;
  }

  @HostListener('mouseleave')
  public mouseleave() {
    this.isHover = false;
  }
}
